import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, SearchableSelect, useToast, v } from '@kss/ui';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { journalEntryService } from '@kss/api';
import type { JournalEntry, JournalEntryCreate, JournalEntryLine } from '@kss/api';
import { decodeId } from '../../utils/idCodec';
import { loadAccountOptions, loadStoreOptions } from '../shared/formLoaders';
import NumberField from '../../components/NumberField';
import { useModulePerms } from '../../hooks/useModulePerms';

/**
 * JournalEntry form — wired to the live schema verified 2026-05-05.
 *
 * Live row uses `narration` (not `description`), int `status` enum
 * (1=Draft, 2=Posted, 3=Reversed), and **requires `lines[]` with at
 * least 2 entries** — the backend 500s with
 * `"Journal entry needs at least 2 lines"` otherwise. The legacy
 * form posted only `reference` / `description` / `isPosted` / no
 * lines and 400'd / 500'd on every save.
 *
 * Design: render an editable lines table where the first two rows
 * are pre-seeded (one debit, one credit) so the form is valid from
 * the moment it opens. Auto-recompute `totalDebit` + `totalCredit`
 * from the lines so they always match.
 */

interface FormLine {
  accountId: string;
  debit: number;
  credit: number;
  description: string;
}

interface FormState {
  entryNumber: string;
  entryDate: string;
  narration: string;
  status: number; // JournalEntryStatus int enum
  storeId: string;
  referenceType: string;
  referenceId: string;
  lines: FormLine[];
}

const today = new Date().toISOString().slice(0, 10);
const emptyLine: FormLine = { accountId: '', debit: 0, credit: 0, description: '' };
const empty: FormState = {
  entryNumber: `JE-${Date.now()}`,
  entryDate: today,
  narration: '',
  status: 1,
  storeId: '',
  referenceType: '',
  referenceId: '',
  lines: [{ ...emptyLine }, { ...emptyLine }],
};

const STATUS_OPTIONS = [
  { value: 1, label: 'Draft' },
  { value: 2, label: 'Posted' },
  { value: 3, label: 'Reversed' },
];

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewJournalEntry() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setLoadError(null);
        try {
          const rec = await journalEntryService.get(id);
          if (cancelled) return;
          const recLines = (rec.lines ?? []).map((l: JournalEntryLine) => ({
            accountId: l.accountId ?? '',
            debit: Number(l.debit ?? 0),
            credit: Number(l.credit ?? 0),
            description: l.description ?? '',
          }));
          setForm({
            entryNumber: rec.entryNumber ?? '',
            entryDate: (rec.entryDate ?? today).slice(0, 10),
            narration: rec.narration ?? rec.description ?? '',
            status: typeof rec.status === 'number' ? rec.status : 1,
            storeId: rec.storeId ?? '',
            referenceType: rec.referenceType ?? '',
            referenceId: rec.referenceId ?? '',
            // Always show at least 2 lines so the form is valid at-rest.
            lines: recLines.length >= 2 ? recLines : [...recLines, ...empty.lines].slice(0, 2),
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setForm({ ...empty, entryNumber: `JE-${Date.now()}` });
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) <= 0.01;
  const validLines = form.lines.filter((l) => l.accountId && (l.debit > 0 || l.credit > 0));
  const perms = useModulePerms('JournalEntry');
  const permitted = isEdit ? perms.canUpdate : perms.canCreate;

  /* Header field errors. The balance and line-count rules below were
     already sound — this form was one of the better ones. What was
     missing was any check on the header: `entryNumber` was presence-
     only, so markup and over-length values passed, and `entryDate` was
     unchecked entirely, so 2026-02-31 posted to the ledger. */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const headerErrors: Record<string, string | null> = {
    entryNumber: v.runRules(form.entryNumber, [
      v.required('Entry number'),
      v.maxLength(50, 'Entry number'),
      ...v.safeText('Entry number'),
    ]),
    entryDate: v.runRules(form.entryDate, [v.required('Date'), v.date({ label: 'Date' })]),
  };
  const headerErr = (k: string) => (touched[k] ? headerErrors[k] ?? undefined : undefined);
  const markT = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const headerValid = !Object.values(headerErrors).some(Boolean);

  const canSave = headerValid && balanced && validLines.length >= 2 && permitted;

  const setLine = (i: number, patch: Partial<FormLine>) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    }));

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const removeLine = (i: number) =>
    setForm((f) =>
      f.lines.length <= 2
        ? f
        : { ...f, lines: f.lines.filter((_, j) => j !== i) },
    );

  async function save() {
    setTouched({ entryNumber: true, entryDate: true });
    const headerProblem = Object.values(headerErrors).find(Boolean);
    if (headerProblem) {
      toast.error('Fix errors', headerProblem);
      return;
    }
    if (!balanced) {
      toast.error('Out of balance', `Debits ₹${totalDebit} ≠ Credits ₹${totalCredit}`);
      return;
    }
    if (validLines.length < 2) {
      toast.error('Need at least 2 lines', 'Each line must have an account and a debit OR credit > 0.');
      return;
    }
    setSaving(true);
    try {
      // Build the live-API canonical payload. Drop legacy keys
      // (reference / description / isPosted / isActive) — the
      // strict-mode binder rejects them with `UNKNOWN_FIELD`.
      const lines: Omit<JournalEntryLine, 'journalEntryLineId' | 'tenantId'>[] = form.lines
        .filter((l) => l.accountId && (l.debit > 0 || l.credit > 0))
        .map((l) => ({
          journalEntryId: '00000000-0000-0000-0000-000000000000',
          accountId: l.accountId,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
          description: l.description || undefined,
        }));
      const body: JournalEntry & { lines: typeof lines } = {
        journalEntryId: isEdit ? id : undefined,
        entryNumber: form.entryNumber,
        entryDate: form.entryDate ? `${form.entryDate}T00:00:00` : undefined,
        narration: form.narration || undefined,
        status: form.status,
        storeId: form.storeId || undefined,
        referenceType: form.referenceType || undefined,
        referenceId: form.referenceId || undefined,
        totalDebit: Math.round(totalDebit * 100) / 100,
        totalCredit: Math.round(totalCredit * 100) / 100,
        lines,
      };
      if (isEdit && id) {
        await journalEntryService.update([body as JournalEntry]);
        toast.success('Saved', `${form.entryNumber} updated`);
      } else {
        const { journalEntryId: _o, ...create } = body;
        await journalEntryService.create([create as JournalEntryCreate]);
        toast.success('Saved', `${form.entryNumber} added`);
      }
      navigate('/panel/journal-entries');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <Card>
        <div className="mb-4 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/journal-entries')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit journal entry' : 'New journal entry'}
          </h2>
        </div>

        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Entry number *</span>
                <input
                  value={form.entryNumber}
                  onChange={(e) => setForm({ ...form, entryNumber: e.target.value })}
                  onBlur={markT('entryNumber')}
                  placeholder="JE-1001"
                  className={`${inputCls}${headerErr('entryNumber') ? ' border-danger' : ''}`}
                />
                {headerErr('entryNumber') && (
                  <span className="text-xs text-danger">{headerErr('entryNumber')}</span>
                )}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Date *</span>
                <input
                  type="date"
                  value={form.entryDate}
                  onChange={(e) => setForm({ ...form, entryDate: e.target.value })}
                  onBlur={markT('entryDate')}
                  className={`${inputCls}${headerErr('entryDate') ? ' border-danger' : ''}`}
                />
                {headerErr('entryDate') && (
                  <span className="text-xs text-danger">{headerErr('entryDate')}</span>
                )}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Status</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                  className={selectCls}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <SearchableSelect
                label="Store"
                value={form.storeId}
                onChange={(v) => setForm({ ...form, storeId: v })}
                loadOptions={loadStoreOptions}
                placeholder="Optional store"
              />
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Reference type</span>
                <input
                  value={form.referenceType}
                  onChange={(e) => setForm({ ...form, referenceType: e.target.value })}
                  placeholder="Invoice / Expense / Manual"
                  className={inputCls}
                />
              </label>
              <label className="flex flex-col gap-1 md:col-span-3">
                <span className="text-xs font-medium text-content-secondary">Narration</span>
                <textarea
                  value={form.narration}
                  onChange={(e) => setForm({ ...form, narration: e.target.value })}
                  rows={2}
                  placeholder="Free-text description for this voucher…"
                  className={`${inputCls} h-auto py-2`}
                />
              </label>
            </div>

            <div className="rounded-md border border-border">
              <div className="flex items-center justify-between border-b border-border px-3 py-2">
                <span className="text-sm font-semibold">Lines (debit / credit splits)</span>
                <Button type="button" size="sm" variant="outline" onClick={addLine}>
                  <Plus className="h-3.5 w-3.5" /> Add line
                </Button>
              </div>
              <div className="divide-y divide-border">
                {form.lines.map((line, i) => (
                  <div key={`line-${i}`} className="grid gap-2 p-3 md:grid-cols-[2fr_1fr_1fr_2fr_auto]">
                    <SearchableSelect
                      label={i === 0 ? 'Account' : undefined}
                      value={line.accountId}
                      onChange={(v) => setLine(i, { accountId: v })}
                      loadOptions={loadAccountOptions}
                      placeholder="Pick an account"
                    />
                    <label className="flex flex-col gap-1">
                      {i === 0 && <span className="text-xs font-medium text-content-secondary">Debit (₹)</span>}
                      <NumberField
                        min={0}
                        step="0.01"
                        value={line.debit}
                        onValueChange={(n) => setLine(i, { debit: n, credit: n > 0 ? 0 : line.credit })}
                        className={inputCls}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      {i === 0 && <span className="text-xs font-medium text-content-secondary">Credit (₹)</span>}
                      <NumberField
                        min={0}
                        step="0.01"
                        value={line.credit}
                        onValueChange={(n) => setLine(i, { credit: n, debit: n > 0 ? 0 : line.debit })}
                        className={inputCls}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      {i === 0 && <span className="text-xs font-medium text-content-secondary">Note</span>}
                      <input
                        value={line.description}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                        placeholder="Optional"
                        className={inputCls}
                      />
                    </label>
                    <div className="flex items-end justify-end">
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        disabled={form.lines.length <= 2}
                        title={form.lines.length <= 2 ? 'At least 2 lines required' : 'Remove line'}
                        className="inline-flex h-9 w-9 items-center justify-center rounded text-danger hover:bg-surface-card-hover disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-6 border-t border-border bg-surface-card-hover px-3 py-2 text-sm">
                <span>
                  Total debit: <b>₹{totalDebit.toLocaleString('en-IN')}</b>
                </span>
                <span>
                  Total credit: <b>₹{totalCredit.toLocaleString('en-IN')}</b>
                </span>
                <span className={balanced ? 'text-success' : 'text-danger'}>
                  {balanced ? 'Balanced' : `Out of balance by ₹${Math.abs(totalDebit - totalCredit).toLocaleString('en-IN')}`}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
              <Button variant="outline" onClick={() => navigate('/panel/journal-entries')}>Cancel</Button>
              <Button onClick={save} disabled={!canSave || saving}>
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
